import { DirectoryGroupingConsts, ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import { featureFlags } from 'client/store/modules/FeatureFlagsModule';
import DirectoryGroupingApi from 'client/api/v2/DirectoryGroupingApi';
import ModelMixin from 'client/store/modules/models/mixins/ModelMixin';
import SystemGroupApi from 'client/api/v2/SystemGroupApi';
import Vue from 'vue';
import createModel from 'client/store/modules/models/createModel';

import * as DynamicGroupsReusable from 'client/store/modules/models/reusables/DynamicGroupsReusable';

const { groupTypes, memberTypes } = DirectoryGroupingConsts;

export const getDefaultItem = () => ({
  id: '',
  name: '',
  passwordLessSudoEnabled: false,
  sudoEnabled: false,
  type: ItemTypeConsts.systemGroup,
  ...DynamicGroupsReusable.getDefaultDynamicGroupItem(),
});

const model = createModel(getDefaultItem, 'id', SystemGroupApi);

export default {
  namespaced: true,

  state: {
    ...model.state,
    ...DynamicGroupsReusable.getDefaultDynamicGroupState(),
    membership: 0,
  },

  actions: {
    ...model.actions,
    setIsPendingReview: DynamicGroupsReusable.setIsPendingReview,

    setMemberQuery: DynamicGroupsReusable.setMemberQuery,
    setMemberQueryUI: DynamicGroupsReusable.setMemberQueryUI,

    updateMembershipMethod: DynamicGroupsReusable.updateMembershipMethod,
    updateMemberSuggestionsNotify: DynamicGroupsReusable.updateMemberSuggestionsNotify,

    addFilter: DynamicGroupsReusable.addFilter,
    updateFilterAttribute: DynamicGroupsReusable.updateFilterAttribute,
    updateFilterOperator: DynamicGroupsReusable.updateFilterOperator,

    addFilterValue: DynamicGroupsReusable.addFilterValue,
    updateFilterValue: DynamicGroupsReusable.updateFilterValue,
    removeFilterValue: DynamicGroupsReusable.removeFilterValue,

    addMemberQueryExemption: DynamicGroupsReusable.addMemberQueryExemption,
    removeMemberQueryExemption: DynamicGroupsReusable.removeMemberQueryExemption,
    updateMemberQueryExemptions: DynamicGroupsReusable.updateMemberQueryExemptions,
    updateExemptionOperation: DynamicGroupsReusable.updateExemptionOperation,
    mapMembershipExemptions: DynamicGroupsReusable.mapMembershipExemptions,
    syncExemptionWithItemSelection: DynamicGroupsReusable.syncExemptionWithItemSelection,

    resetIdIncrement: DynamicGroupsReusable.resetIdIncrement,

    resetMemberQueryUI: DynamicGroupsReusable.resetMemberQueryUI,

    resetMembershipData: DynamicGroupsReusable.resetMembershipData,

    setPreviousItemMemberQueryUI: DynamicGroupsReusable.setPreviousItemMemberQueryUI,

    bindSudoStatus(context) {
      const { id } = context.getters;
      const targetsToUpdate = context.getters.getSudoersToUpdate;

      const payload = {
        parentType: groupTypes.systemGroup,
        parentId: id,
        targetType: memberTypes.system,
        targetsToUpdate,
      };

      return context.dispatch(
        'DirectoryGroupingModule/bindAttributes',
        payload,
        { root: true },
      );
    },

    bindSystemGroupToSystems(context) {
      return context.dispatch('bindAssociations', {
        parentType: groupTypes.systemGroup,
        targetType: memberTypes.system,
      });
    },

    bindSystemGroupToUserGroups(context) {
      return context.dispatch('bindAssociations', {
        parentType: groupTypes.systemGroup,
        targetType: groupTypes.userGroup,
      });
    },

    bindSystemGroupToPolicies(context) {
      return context.dispatch('bindAssociations', {
        parentType: groupTypes.systemGroup,
        targetType: ItemTypeConsts.policy,
      });
    },

    bindSystemGroupToConfigurationGroups(context) {
      return context.dispatch('bindAssociations', {
        parentType: groupTypes.systemGroup,
        targetType: ItemTypeConsts.configurationGroup,
      });
    },

    async getSystemGroupAssociations(context, { parentId, targetType }) {
      const parentType = groupTypes.systemGroup;
      const associations = await DirectoryGroupingApi
        .getAssociations(parentType, parentId, targetType);

      return associations;
    },

    async fetchItem(context) {
      await model.actions.fetchItem(context);

      const { dispatch, rootGetters } = context;

      const betaFeatures = rootGetters['FeatureFlagsModule/betaFeatures'];
      const dynamicDeviceGroupsUI = betaFeatures[featureFlags.dynamicDeviceGroupsUI];

      if (dynamicDeviceGroupsUI) {
        await dispatch('mapMembershipExemptions', ItemTypeConsts.systemGroup);
      }
    },

    fetchMembership({ commit }, systemGroupId) {
      return SystemGroupApi.getMembership(systemGroupId)
        .then((response) => {
          commit('UPDATE_MEMBERSHIP', response.headers['x-total-count']);
        });
    },

    async fetchSystemGroupItem(_, { id }) {
      const systemGroup = id ? await SystemGroupApi.get(id) : {};

      return systemGroup;
    },

    async handleGroupBindings({ dispatch }) {
      await Promise.all([
        dispatch('bindSystemGroupToConfigurationGroups'),
        dispatch('bindSystemGroupToPolicies'),
        dispatch('bindSystemGroupToSystems'),
        dispatch('bindSystemGroupToUserGroups'),
      ]);
    },

    async saveItem(context) {
      const {
        commit,
        dispatch,
        getters,
        rootGetters,
      } = context;

      const betaFeatures = rootGetters['FeatureFlagsModule/betaFeatures'];
      const dynamicDeviceGroupsUI = betaFeatures[featureFlags.dynamicDeviceGroupsUI];

      let exemptions;
      if (dynamicDeviceGroupsUI) {
        await dispatch('setMemberQuery');

        exemptions = getters.getMembershipExemptions;
      }

      const item = await model.actions.saveItem(context);

      if (dynamicDeviceGroupsUI) {
        // Because `createModel.actions.saveItem` refreshes the "item" state field with
        // the newly retrieved API data for the "item", and the exemptions' "op" field only
        // exist on the FE, we see the exemptions disappear when the "item" state gets refreshed
        // with the API data. So after we save the item, we re-update the exemptions field with
        // the FE version of the "exemptions" data field.
        commit('UPDATE_MEMBER_QUERY_EXEMPTIONS', { newValue: exemptions });
      }

      return item;
    },
  },

  getters: {
    ...model.getters,
    ...ModelMixin.getters,

    getIsPendingReview: DynamicGroupsReusable.getIsPendingReview,
    getHasMembershipChanged: DynamicGroupsReusable.getHasMembershipChanged,
    getHasMembershipMethodChanged: DynamicGroupsReusable.getHasMembershipMethodChanged,
    getHaveMembershipFiltersChanged: DynamicGroupsReusable.getHaveMembershipFiltersChanged,
    getMemberSuggestionsNotify: DynamicGroupsReusable.getMemberSuggestionsNotify,
    getMembershipMethod: DynamicGroupsReusable.getMembershipMethod,
    getMembershipFilters: DynamicGroupsReusable.getMembershipFilters,
    getFilterByIndex: DynamicGroupsReusable.getFilterByIndex,
    getMemberQueryUI: DynamicGroupsReusable.getMemberQueryUI,
    getMembershipExemptions: DynamicGroupsReusable.getMembershipExemptions,
    getPreviousMembershipExemptions: DynamicGroupsReusable.getPreviousMembershipExemptions,
    getMembershipExemptionsIds: DynamicGroupsReusable.getMembershipExemptionsIds,
    getMembershipExclusions: DynamicGroupsReusable.getMembershipExclusions,
    getMembershipInclusions: DynamicGroupsReusable.getMembershipInclusions,
    getHaveExemptionsChanged: DynamicGroupsReusable.getHaveExemptionsChanged,

    displayName(state) {
      return state.item.name;
    },

    getSudoersToUpdate(state, getters, rootState, rootGetters) {
      const module = StoreModuleMap[ItemTypeConsts.userGroup];
      return rootGetters[`${module}/getSudoersToUpdate`];
    },

    haveAssociationsChanged(state, getters) {
      const associationTypes = [
        memberTypes.system,
        groupTypes.userGroup,
        ItemTypeConsts.policy,
        ItemTypeConsts.configurationGroup,
      ];

      const sudoersToUpdate = getters.getSudoersToUpdate;

      if (sudoersToUpdate && Object.keys(sudoersToUpdate).length) return true;

      return model.getters.haveAssociationsChanged(state, getters)(associationTypes);
    },

    getUserGroupAssociationCounts(state, getters) {
      const userGroupsToBind = getters.getTargetsToBind(groupTypes.userGroup);
      const userGroupsToUnbind = getters.getTargetsToUnbind(groupTypes.userGroup);
      return { bind: userGroupsToBind.length, unbind: userGroupsToUnbind.length };
    },

    membershipCount(state) {
      return state.membership;
    },
  },

  mutations: {
    ...model.mutations,

    SET_IS_PENDING_REVIEW: DynamicGroupsReusable.SET_IS_PENDING_REVIEW,

    UPDATE_MEMBERSHIP_METHOD: DynamicGroupsReusable.UPDATE_MEMBERSHIP_METHOD,
    UPDATE_MEMBER_SUGGESTIONS_NOTIFY: DynamicGroupsReusable.UPDATE_MEMBER_SUGGESTIONS_NOTIFY,

    ADD_FILTER: DynamicGroupsReusable.ADD_FILTER,
    UPDATE_ATTRIBUTE: DynamicGroupsReusable.UPDATE_ATTRIBUTE,
    UPDATE_OPERATOR: DynamicGroupsReusable.UPDATE_OPERATOR,

    ADD_FILTER_VALUE: DynamicGroupsReusable.ADD_FILTER_VALUE,
    UPDATE_FILTER_VALUE: DynamicGroupsReusable.UPDATE_FILTER_VALUE,
    REMOVE_FILTER_VALUE: DynamicGroupsReusable.REMOVE_FILTER_VALUE,
    RESET_FILTER_VALUES: DynamicGroupsReusable.RESET_FILTER_VALUES,
    RESET_FILTER_VALUES_EXCEPT_FIRST: DynamicGroupsReusable.RESET_FILTER_VALUES_EXCEPT_FIRST,

    ADD_MEMBER_QUERY_EXEMPTION: DynamicGroupsReusable.ADD_MEMBER_QUERY_EXEMPTION,
    REMOVE_MEMBER_QUERY_EXEMPTION: DynamicGroupsReusable.REMOVE_MEMBER_QUERY_EXEMPTION,
    UPDATE_MEMBER_QUERY_EXEMPTIONS: DynamicGroupsReusable.UPDATE_MEMBER_QUERY_EXEMPTIONS,
    UPDATE_EXEMPTION_OPERATION: DynamicGroupsReusable.UPDATE_EXEMPTION_OPERATION,

    INCREMENT_ID: DynamicGroupsReusable.INCREMENT_ID,
    RESET_ID_INCREMENT: DynamicGroupsReusable.RESET_ID_INCREMENT,

    RESET_MEMBER_QUERY_UI: DynamicGroupsReusable.RESET_MEMBER_QUERY_UI,

    RESET_MEMBERSHIP_DATA: DynamicGroupsReusable.RESET_MEMBERSHIP_DATA,

    SET_MEMBER_QUERY: DynamicGroupsReusable.SET_MEMBER_QUERY,

    SET_PREVIOUS_ITEM_MEMBER_QUERY_UI: DynamicGroupsReusable.SET_PREVIOUS_ITEM_MEMBER_QUERY_UI,
    SET_PREVIOUS_ITEM_FIELD: ModelMixin.mutations.SET_PREVIOUS_ITEM_FIELD,

    UPDATE_MEMBERSHIP(state, membership) {
      Vue.set(state, 'membership', membership);
    },
  },
};
